import { dispatch } from "../store/store";
import { logErrorWithPayload } from "../services/logService";
import {
  getCompanyProductRulesByCompanyIdData,
  addCompanyProductRuleData,
  editCompanyProductRuleData,
  searchProductDrugData,
  removeCompanyProductRuleData,
  getProductRuleAuditData,
  getMonthlyConcessionData,
  getHistoricalConcessionData,
  addConcessionData,
  getTitanClaimsData,
  getPrescriptionImageData,
  getItemInfoData,
  saveClaimsData,
  processClaimData,
  replacementDispenseNotificationData,
  bulkRtecData,
  getClaimedCurrentData,
  getClaimedLastData,
  claimAmendmentData,
  getBlockedClaimsData,
  addEndorsementData,
  addUpdateExemptionData,
  getExemptionData,
  bulkExemptionData,
  bulkClearClaimsData,
  getExpiringItemsData,
  saveClaimPrescriptionItemsData,
  getClaimPrescriptionItemsData,
  getConcessionPricesData,
  getDashboardStatsData,
  setIsTitanTabLoading,
  setCompanyProductRules,
  getGroupModeServicesData,
  getFp34SummaryData,
  getFp34AdditionalFeesData,
  getPharmacyPrivateServicesIncomeData,
  getPharmacyPrivateServicesIncomeByDateRangeData,
  getDispensingIncomeData
} from "../features/titanTabSlice/titanTabSlice";

const reducerName = "titanTab";

// Axios/network failures carry a technical message that means nothing to a
// pharmacist; anything else is a message the API itself wrote (e.g. "Your
// account is not linked to Titan") and is worth showing verbatim.
const TECHNICAL_ERROR = /^(request failed with status code|network error|timeout of|timeout exceeded|canceled|request aborted)/i;

// The API rejects a `success: false` body with the body itself, so the
// server message survives as `error.message` once the thunk serialises it.
export const apiErrorMessage = (error) => {
  const message = String(
    error?.response?.data?.message ?? error?.message ?? "",
  ).trim();
  return message && !TECHNICAL_ERROR.test(message) ? message : "";
};

// #region State Setters

export const setTitanTabLoading = (isLoading) => {
  dispatch(setIsTitanTabLoading(isLoading));
};

export const setCompanyProductRulesData = (data) => {
  dispatch(setCompanyProductRules(data));
};

// #endregion

// #region API getter/caller functions for components usage to dispatch REDUX extra reducers

export const getCompanyProductRulesByCompanyId = async (companyId) => {
  let result = [];
  try {
    result = await dispatch(
      getCompanyProductRulesByCompanyIdData(companyId)
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyId);
    result = [];
  }
  return result;
};

export const addCompanyProductRule = async (payload) => {
  let result = null;
  try {
    result = await dispatch(addCompanyProductRuleData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const editCompanyProductRule = async (payload) => {
  let result = null;
  try {
    result = await dispatch(editCompanyProductRuleData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const searchProductDrug = async (keyword) => {
  let result = [];
  try {
    result = await dispatch(searchProductDrugData(keyword)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, keyword);
    result = [];
  }
  return result;
};

export const removeCompanyProductRule = async (prescribedItemId, productRuleId) => {
  let result = null;
  try {
    result = await dispatch(
      removeCompanyProductRuleData({ prescribedItemId, productRuleId })
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, { prescribedItemId, productRuleId });
    result = null;
  }
  return result;
};

export const getHistoricalConcession = async () => {
  let result = [];
  try {
    result = await dispatch(getHistoricalConcessionData()).unwrap();
  } catch (error) {
    logErrorWithPayload(error);
    result = [];
  }
  return result;
};

export const addConcession = async (payload) => {
  let result = null;
  try {
    result = await dispatch(addConcessionData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const getMonthlyConcession = async (concessionMonth) => {
  let result = [];
  try {
    result = await dispatch(getMonthlyConcessionData(concessionMonth)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, concessionMonth);
    result = [];
  }
  return result;
};

export const getProductRuleAudit = async (prescribedItemId, productRuleId) => {
  let result = null;
  try {
    result = await dispatch(
      getProductRuleAuditData({ prescribedItemId, productRuleId })
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, { prescribedItemId, productRuleId });
    result = null;
  }
  return result;
};

export const getTitanClaims = async (pharmacyId, onError) => {
  let result = [];
  try {
    result = await dispatch(getTitanClaimsData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    onError?.(apiErrorMessage(error));
    result = null;
  }
  return result;
};

export const getPrescriptionImage = async (pharmacyId, incomingId) => {
  let result = [];
  try {
    const respData = await dispatch(
      getPrescriptionImageData({ pharmacyId, incomingId })
    ).unwrap();
    result = Array.isArray(respData)
      ? respData
      : respData?.response ?? respData?.data ?? [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, incomingId });
    result = [];
  }
  return result;
};

// Product/pack candidates for a set of prescribed items (get-item-info).
export const getItemInfo = async (pharmacyId, prescribedItems) => {
  try {
    const respData = await dispatch(
      getItemInfoData({ pharmacyId, prescribedItems })
    ).unwrap();
    if (Array.isArray(respData)) return respData;
    return respData?.response ?? respData?.data ?? [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescribedItems });
    return [];
  }
};

// FP34 total summary for a pharmacy/cycle (get-fp34-summary).
export const getFp34Summary = async (pharmacyId, cycleMonth, onError) => {
  try {
    const respData = await dispatch(
      getFp34SummaryData({ pharmacyId, cycleMonth })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, cycleMonth });
    onError?.(apiErrorMessage(error));
    return null;
  }
};

// FP34 additional fee items for a pharmacy/cycle (get-fp34-additional-fees).
export const getFp34AdditionalFees = async (pharmacyId, cycleMonth, onError) => {
  try {
    const respData = await dispatch(
      getFp34AdditionalFeesData({ pharmacyId, cycleMonth })
    ).unwrap();
    const items = respData?.response ?? respData?.data ?? respData;
    return Array.isArray(items) ? items : [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, cycleMonth });
    onError?.(apiErrorMessage(error));
    return null;
  }
};

// Private services income breakdown for a pharmacy/month (get-pharmacy-private-services-income).
export const getPharmacyPrivateServicesIncome = async (companyId, pharmacyId, month) => {
  try {
    const respData = await dispatch(
      getPharmacyPrivateServicesIncomeData({ companyId, pharmacyId, month })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { companyId, pharmacyId, month });
    return null;
  }
};

export const getPharmacyPrivateServicesIncomeByDateRange = async (
  companyId,
  pharmacyId,
  startDate,
  endDate,
) => {
  try {
    const respData = await dispatch(
      getPharmacyPrivateServicesIncomeByDateRangeData({ companyId, pharmacyId, startDate, endDate })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { companyId, pharmacyId, startDate, endDate });
    return null;
  }
};

export const getDispensingIncome = async (pharmacyId, fromDate, toDate, onError) => {
  try {
    const respData = await dispatch(
      getDispensingIncomeData({ pharmacyId, fromDate, toDate })
    ).unwrap();
    const data = respData?.response ?? respData?.data ?? respData;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, fromDate, toDate });
    onError?.(apiErrorMessage(error));
    return null;
  }
};

export const saveClaims = async (pharmacyId, prescriptionIds) => {
  let result = null;
  try {
    const respData = await dispatch(
      saveClaimsData({ pharmacyId, prescriptionIds })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionIds });
    result = null;
  }
  return result;
};

export const processClaim = async (pharmacyId, claim) => {
  let result = null;
  try {
    const respData = await dispatch(
      processClaimData({ pharmacyId, claim })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, claim });
    result = null;
  }
  return result;
};

export const replacementDispenseNotification = async (pharmacyId, request) => {
  let result = null;
  try {
    const respData = await dispatch(
      replacementDispenseNotificationData({ pharmacyId, request })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, request });
    result = null;
  }
  return result;
};

export const bulkRtec = async (pharmacyId, incomingPrescriptionIds) => {
  let result = null;
  try {
    const respData = await dispatch(
      bulkRtecData({
        pharmacyId,
        request: {
          spineApiRequest: { iaToken: "", testMode: true },
          incomingPrescriptionIds,
        },
      })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, incomingPrescriptionIds });
    result = null;
  }
  return result;
};

export const getClaimedCurrent = async (pharmacyId, onError) => {
  let result = [];
  try {
    result = await dispatch(getClaimedCurrentData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    onError?.(apiErrorMessage(error));
    result = null;
  }
  return result;
};

export const getClaimedLast = async (pharmacyId, onError) => {
  let result = [];
  try {
    result = await dispatch(getClaimedLastData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    onError?.(apiErrorMessage(error));
    result = null;
  }
  return result;
};

export const claimAmendment = async (pharmacyId, request) => {
  let result = null;
  try {
    const respData = await dispatch(
      claimAmendmentData({ pharmacyId, request })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, request });
    result = null;
  }
  return result;
};

export const getBlockedClaims = async (pharmacyId, onError) => {
  let result = [];
  try {
    result = await dispatch(getBlockedClaimsData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    onError?.(apiErrorMessage(error));
    result = null;
  }
  return result;
};

export const addEndorsement = async (pharmacyId, request) => {
  let result = null;
  try {
    const respData = await dispatch(
      addEndorsementData({ pharmacyId, request })
    ).unwrap();
    result = respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, request });
    result = null;
  }
  return result;
};

export const addUpdateExemption = async (pharmacyId, exemption) => {
  let result = null;
  try {
    const respData = await dispatch(
      addUpdateExemptionData({ pharmacyId, exemption })
    ).unwrap();
    result = respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, exemption });
    result = null;
  }
  return result;
};

export const getExemption = async (pharmacyId, prescriptionId) => {
  try {
    const respData = await dispatch(
      getExemptionData({ pharmacyId, prescriptionId })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionId });
    return null;
  }
};

export const getExpiringItems = async (pharmacyId) => {
  try {
    const respData = await dispatch(getExpiringItemsData(pharmacyId)).unwrap();
    const data = respData?.response ?? respData?.data ?? respData;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    return null;
  }
};

export const bulkExemption = async (pharmacyId, request) => {
  try {
    const respData = await dispatch(
      bulkExemptionData({ pharmacyId, request })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, request });
    return null;
  }
};

export const bulkClearClaims = async (pharmacyId, prescriptionIds, onError) => {
  try {
    const respData = await dispatch(
      bulkClearClaimsData({ pharmacyId, prescriptionIds })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionIds });
    onError?.(apiErrorMessage(error));
    return null;
  }
};

export const saveClaimPrescriptionItems = async (pharmacyId, request) => {
  try {
    const respData = await dispatch(
      saveClaimPrescriptionItemsData({ pharmacyId, request })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, request });
    return null;
  }
};

export const getClaimPrescriptionItems = async (pharmacyId, prescriptionId) => {
  try {
    const respData = await dispatch(
      getClaimPrescriptionItemsData({ pharmacyId, prescriptionId })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionId });
    return null;
  }
};

export const getConcessionPrices = async (pharmacyId, prescribedItems) => {
  try {
    const respData = await dispatch(
      getConcessionPricesData({ pharmacyId, prescribedItems })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescribedItems });
    return null;
  }
};

export const getGroupModeServices = async (companyID) => {
  let result = [];
  try {
    result = await dispatch(getGroupModeServicesData(companyID)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyID);
    result = [];
  }
  return result;
};


export const getDashboardStats = async (companyId) => {
  let result = null;
  try {
    result = await dispatch(getDashboardStatsData(companyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyId);
    result = null;
  }
  return result;
};

// #endregion
